import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Popover,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useExcessStockItems } from '../hooks/useDashboardData';

export const ExcessStockTable: React.FC = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<Record<string, string> | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useExcessStockItems(page + 1, rowsPerPage, debouncedSearchTerm);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenProperties = (event: React.MouseEvent<HTMLElement>, properties: Record<string, string>) => {
    setAnchorEl(event.currentTarget);
    setSelectedProperties(properties);
  };

  const handleCloseProperties = () => {
    setAnchorEl(null);
    setSelectedProperties(null);
  };

  const handleGoToUnitDetails = (unitId: string) => {
    // TODO: Navigate to unit details page
    console.log('Navigate to unit details:', unitId);
    // Example: navigate(`/units/${unitId}`);
  };

  const openPopover = Boolean(anchorEl);

  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Excess Stock Items" />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader title="Excess Stock Items" />
        <Box sx={{ p: 2 }}>
          <Alert severity="error">Failed to load excess stock items</Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Excess Stock Items"
        action={
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        }
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Item Name</TableCell>
              <TableCell>Barcode</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Warehouse</TableCell>
              <TableCell align="right">Current Stock</TableCell>
              <TableCell align="right">Max Stock</TableCell>
              <TableCell align="right">Overage</TableCell>
              <TableCell>Properties</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!data?.items || data.items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No excess stock items found
                </TableCell>
              </TableRow>
            ) : (
              data.items.map((item, index) => {
                const propertyEntries = item.additionalProperties
                  ? Object.entries(item.additionalProperties)
                  : [];
                const displayedProperties = propertyEntries.slice(0, 2);
                const hasMoreProperties = propertyEntries.length > 2;

                return (
                  <TableRow key={`${item.unitId}-${index}`} hover>
                    <TableCell>{item.itemName}</TableCell>
                    <TableCell>{item.barcode}</TableCell>
                    <TableCell>{item.symbol}</TableCell>
                    <TableCell>{item.warehouseName}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={item.onHandQuantity}
                        size="small"
                        sx={{
                          backgroundColor: theme.colors.alpha.warning[10],
                          color: theme.colors.warning.main,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{item.maxNumber}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={Math.abs(item.variance)}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                        {displayedProperties.map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                        {hasMoreProperties && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenProperties(e, item.additionalProperties!)}
                            sx={{ ml: 0.5 }}
                          >
                            <MoreHorizIcon fontSize="small" />
                          </IconButton>
                        )}
                        {propertyEntries.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Go to Unit Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleGoToUnitDetails(item.unitId)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data?.totalCount || 0}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handleCloseProperties}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 400 }}>
          <Typography variant="subtitle2" gutterBottom>
            All Properties
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {selectedProperties &&
              Object.entries(selectedProperties).map(([key, value]) => (
                <Box key={key} sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {key}:
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Box>
              ))}
          </Box>
        </Box>
      </Popover>
    </Card>
  );
};
